import type { Capture, CaptureType } from '@daily/shared';
import { format } from 'date-fns';
import { stableImageUrl } from '@/lib/stableUrl';

interface CaptureTileProps {
  capture: Capture;
  onClick?: () => void;
}

const TYPE_LABEL: Record<CaptureType, string> = {
  DRAWING: 'Drawing',
  SIENA_WRITING: "Siena's writing",
  LETTER: 'Letter',
  INVITE: 'Invite',
  CARD: 'Card',
  CERTIFICATE: 'Certificate',
  FLYER: 'Flyer',
  OTHER: 'Captured',
};

// Frame palette — picked from theme.ts. Each frame has a body colour for the
// border around the photo, a tape strip colour for the top, and a rotation so
// the wall feels like a corkboard, not a CMS grid.
interface Frame {
  bg: string;
  tape: string;
  rotate: string;
  inkOnFrame?: string;
}

const FRAMES: Frame[] = [
  { bg: '#FFB088', tape: '#E8A33A', rotate: '-1.6deg' },               // peach + mustard tape
  { bg: '#F8B6C2', tape: '#E94B6B', rotate: '1.2deg' },                // roseSoft + rose tape
  { bg: '#F5C97A', tape: '#C9572D', rotate: '-0.8deg' },               // mustardSoft + terracotta
  { bg: '#B89FD4', tape: '#5B2A4A', rotate: '1.8deg', inkOnFrame: '#FFFFFF' }, // lilac + plum
  { bg: '#4A8589', tape: '#1B5E62', rotate: '-1.2deg', inkOnFrame: '#FFFFFF' }, // tealSoft + teal
  { bg: '#6B8C5E', tape: '#4A6B3F', rotate: '0.9deg', inkOnFrame: '#FFFFFF' }, // sage + sageDeep
  { bg: '#FFD4B8', tape: '#E94D2C', rotate: '-2deg' },                 // peachSoft + tomato
  { bg: '#FBF4E8', tape: '#5B2A4A', rotate: '1.4deg' },                // bg cream + plum tape
];

// Stable string hash so a capture always gets the same frame across reloads.
function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function deadlineFrom(capture: Capture): string | null {
  const meta = (capture.metadata ?? {}) as Record<string, unknown>;
  // Only show a deadline pill for actionable dates — match parser pin logic.
  // event_date is informational (e.g. "head bump happened on the 18th") and
  // shouldn't surface as a deadline.
  const raw = meta.deadline ?? meta.rsvp_by ?? meta.date_time;
  if (typeof raw !== 'string') return null;
  const d = new Date(raw);
  if (isNaN(d.getTime()) || d <= new Date()) return null;
  try {
    return format(d, 'EEE d MMM');
  } catch {
    return null;
  }
}

export function CaptureTile({ capture, onClick }: CaptureTileProps) {
  const label = TYPE_LABEL[capture.captureType];
  const frame = FRAMES[hashId(capture.id) % FRAMES.length]!;
  const deadline = deadlineFrom(capture);
  // Pin the URL per capture id so the 60s dashboard poll (which re-signs the
  // S3 URL) doesn't re-flash the image.
  const photoUrl = stableImageUrl(capture.id, capture.thumbnailUrl ?? capture.originalUrl);
  const ink = frame.inkOnFrame ?? '#1A1410';

  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative rounded-md p-2.5 pt-4 pb-3 flex flex-col gap-2 transition-transform duration-300 ease-out hover:!rotate-0 hover:scale-[1.03] hover:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-tomato"
      style={{
        background: frame.bg,
        transform: `rotate(${frame.rotate})`,
        boxShadow:
          '0 12px 28px -10px rgba(26,20,16,0.28), 0 3px 6px rgba(26,20,16,0.10)',
        color: ink,
      }}
      aria-label={`${label}: ${capture.caption ?? 'open'}`}
    >
      {/* Tape strip across the top — slightly skewed, like masking tape */}
      <span
        aria-hidden
        className="absolute -top-2 left-1/2 w-12 h-4 -translate-x-1/2 -rotate-[3deg] z-[2] rounded-sm"
        style={{
          background: frame.tape,
          boxShadow: '0 2px 4px rgba(26,20,16,0.15)',
          opacity: 0.92,
        }}
      />

      {deadline && (
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase tracking-[0.04em] text-card bg-tomatoDeep/95 z-[3] shadow-sm">
          ⏱ {deadline}
        </div>
      )}

      {/* Photo */}
      <div
        className="relative rounded-sm overflow-hidden aspect-square shadow-inner"
        style={{
          background: photoUrl ? '#FFFDF5' : 'rgba(255,255,255,0.55)',
          backgroundImage: photoUrl ? `url(${photoUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Caption in script — the kid-handwriting feel */}
      <div className="flex flex-col items-center px-1 min-h-[34px]">
        <div
          className="font-script text-[17px] text-center leading-tight line-clamp-2"
          style={{ color: ink }}
        >
          {capture.caption ?? label}
        </div>
        <div
          className="text-[8px] font-sans uppercase tracking-[0.18em] opacity-60 mt-0.5"
          style={{ color: ink }}
        >
          {label}
        </div>
      </div>
    </button>
  );
}
